var GiftWithPurchase = class extends HTMLElement {
  constructor() {
    super();
    this.gifts = JSON.parse(this.querySelector('[data-gifts-json]').innerHTML)?.filter(gift => gift.available)
    this.cartTotal = 0
    this.idsInCart = []
    this._onCartChangedListener = this._onCartChanged.bind(this)
    this.getCart()
  }

  async getCart () {
    const cartJson = await (await fetch(`${Shopify.routes.root}cart.js`)).json()
    this.cartData = cartJson
    this.cartItems = cartJson.items

    this.updateCartVariables()
    this.checkGifts()
    this.removeStaleGifts()
  }

  async connectedCallback() {
    this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
    document.addEventListener("cart:change", this._onCartChangedListener);
    // Listen for the explicit removal of any gift with purchase.
    document.addEventListener('click', this.removeAndIgnoreGiftListener);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChangedListener);
    document.removeEventListener('click', this.removeAndIgnoreGiftListener);
  }

  /**
   * Handles the event of a gift being removed from the cart.
   * @param {Event} event
   */
  removeAndIgnoreGiftListener = (event) => {
    const linkTarget = event.target.closest('.line-item__remove');
    if (!linkTarget) {
      return;
    }

    const giftVariantId = linkTarget.getAttribute('data-gift-variant-id');
    if (!giftVariantId || !event.target.closest('line-item-quantity')) {
      // Not a gift being removed.
      return;
    }

    this.ignoreGift(giftVariantId);
  }

  /**
   * Marks a gift as ignored by storing its identifier in session storage.
   * @param {string} variantId - The gift's variant identifier to be ignored.
   */
  ignoreGift = (variantId) => {
    sessionStorage.setItem(`gwp-${variantId}`, ''); // No value needed.
  }

  /**
   * Checks if a gift is marked as ignored in session storage.
   * @param {string} variantId - The gift's variant identifier to check.
   * @returns {boolean} - Returns `true` if the gfit is ignored, otherwise `false`.
   */
  giftIsIgnored = (variantId) => {
    return sessionStorage.getItem(`gwp-${variantId}`) !== null;
  }

  /**
   * Removes the ignored status of a gift by removing it from session storage.
   * @param {string} variantId - The gift's variant identifier to allow.
   */
  allowGift = (variantId) => {
    sessionStorage.removeItem(`gwp-${variantId}`);
  }

  _onCartChanged(event) {
    this.cartItems = event.detail.cart.items
    this.updateCartVariables()
    this.checkGifts(event.detail.baseEvent);
    this.removeStaleGifts()
  }

  /**
   * Manages the gift variants in the cart based on promo requirements and specific cart events.
   * @async
   * @param {string} eventType - The type of event triggering the gift check, e.g. `variant:add`.
   */
  async checkGifts(eventType) {
    let reload = false
    let modify = false
    const atcData = { items: [] }
    const changeData = { updates: {} }
    // A gift stops being ignored if its qualifying product is attached with one of the following events.
    const targetEventTypes = ['variant:add'];

    const giftsByVariantId = new Map();

    this.gifts.forEach(gift => {
      const variantId = gift.id;
      const hasValidRequirements = this.checkGiftRequirementMet(gift);

      if (!giftsByVariantId.has(variantId)) {
        giftsByVariantId.set(variantId, {
          variant_id: variantId,
          type: gift.type,
          requirement: gift.requirement,
          promotions: [],
          has_any_valid_requirements: false,
          has_new_subscriber_promotion: false
        });
      }

      const giftGroup = giftsByVariantId.get(variantId);
      giftGroup.promotions.push({
        type: gift.type,
        requirement: gift.requirement,
        has_valid_requirements: hasValidRequirements
      });

      if (hasValidRequirements) {
        giftGroup.has_any_valid_requirements = true;
      }

      if (gift.type === 'new_subscriber') {
        giftGroup.has_new_subscriber_promotion = true;
        if (hasValidRequirements) {
          giftGroup.type = 'new_subscriber';
        }
      }
    });

    const giftVariants = Array.from(giftsByVariantId.values());

    // Lookahead to see whether a `new_subscriber` gift should be added.
    const newSubscriberGift = this.gifts.find(gift =>
      gift.type === 'new_subscriber' && this.checkGiftRequirementMet(gift)
    );

    for (const giftVariant of giftVariants) {
      if (giftVariant.has_any_valid_requirements && targetEventTypes.includes(eventType)) {
        // Adding a qualifying product to your cart will always allow the gift.
        this.allowGift(giftVariant.variant_id);
      }

      if (this.giftIsIgnored(giftVariant.variant_id)) {
        continue;
      }

      const giftInCart = this.getGiftInCart(giftVariant.variant_id);

      // Skip other gifts if a `new_subscriber` gift is valid.
      if (newSubscriberGift && !giftVariant.has_new_subscriber_promotion) {
        // Remove a previous gift because the `new_subscriber` gift takes priority.
        if (giftInCart) {
          changeData.updates[giftInCart.key] = 0;
          modify = true;
        }
        continue;
      }

      // Process the current gift based on its validity and presence in the cart.
      if (giftVariant.has_any_valid_requirements && !giftInCart) {
        atcData.items.push({
          id: giftVariant.variant_id,
          quantity: 1,
          properties: {
            '_gift-with-purchase': true
          },
        });
      } else if (!giftVariant.has_any_valid_requirements && giftInCart) {
        changeData.updates[giftInCart.key] = 0;
        modify = true;
      } else if (giftVariant.has_any_valid_requirements && giftInCart && giftInCart.quantity > 1) {
        changeData.updates[giftInCart.key] = 1;
        modify = true;
      }
    }

    // Add new gifts to the cart.
    if (atcData.items.length) {
      await this.addToCart(atcData);
      reload = true;
    }

    // Modify the cart to remove stale or conflicting gifts.
    if (modify) {
      await this.modifyGiftInCart(changeData);
      reload = true;
    }

    if (window.themeVariables.settings.pageType !== 'cart' || !reload) {
      return;
    }

    window.location.reload();
  }

  // Remove GWP items when associated GWP metaobject is deleted / disabled
  async removeStaleGifts() {
    const changeData = { updates: {} }
    for (const item of this.cartItems) {
      if (Object.hasOwn(item.properties, '_gift-with-purchase') && !this.gifts.some(gift => gift.id === item.variant_id)) {
        changeData.updates[item.key] = 0
      }
    }

    if (Object.keys(changeData.updates).length) {
      await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(changeData)
      })
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    }
  }

  checkGiftRequirementMet (gift) {
    if (gift.type === 'Cart Total') {
      return this.cartTotal >= gift.requirement

    } else if (gift.type === 'Item in Cart') {
      return gift.requirement.split(',').some(id => this.idsInCart.includes(Number(id)))

    } else if (gift.type === 'new_subscriber') {
      // If the user is logged in and tagged as such.
      const isSubscriber = gift.requirement.includes('ordergroove-subscriber') ||
        gift.requirement.includes('Active Ordergroove Subscriber');
      // If the cart has any subscription products.
      const cartQualifies = this.cartItems.some((item) => item.selling_plan_allocation);
      return !isSubscriber && cartQualifies;

    } else if (!gift.type) {
      return false
    }

    return false
  }

  async addToCart (data) {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: JSON.stringify(data),
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    const responseJson = await response.json()
    if (response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    } else {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
    }
  }

  async modifyGiftInCart (data) {
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data)
    })
    if (response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    }
  }

  getGiftInCart (giftVariantId) {
    return this.cartItems.find(item => item.id === giftVariantId && Object.hasOwn(item.properties, '_gift-with-purchase'))
  }

  updateCartVariables () {
    this.cartTotal = this.cartItems.reduce((total, item) => {
      if (Object.hasOwn(item.properties, '_gift-with-purchase')) return total
      return total + item.final_line_price
    }, 0)

    this.idsInCart = this.cartItems.filter((item) => item.properties['_gift-with-purchase'] !== true).map(item => item.id)
  }
};
if (!window.customElements.get('gift-with-purchase')) {
  window.customElements.define('gift-with-purchase', GiftWithPurchase);
}

var LineItemVariantSelector = class extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select')
    this.key = this.dataset.key
    this.value = this.select.value
    this.getCart()
  }

  async getCart() {
    const cartJson = await (await fetch(`${Shopify.routes.root}cart.js`)).json()
    this.cartItems = cartJson.items
    this.init()
  }

  init () {
    this.cartItem = this.cartItems.find(item => item.key === this.key)
    this.select.addEventListener('change', this.changeVariant.bind(this))
  }

  async changeVariant () {
    if (this.select.value === this.value) { return }
    const changeData = {
      id: this.cartItem.key,
      quantity: 0
    }

    const addData = {
      items: [
        {
          id: this.select.value,
          quantity: this.cartItem.quantity,
          ...(this.cartItem.selling_plan_allocation?.selling_plan?.id) && { selling_plan: this.cartItem.selling_plan_allocation.selling_plan.id }
        }
      ]
    }

    // Remove Old Item
    await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(changeData)
    })

    // Add New Item
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: JSON.stringify(addData),
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    const responseJson = await response.json()
    if (response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    } else {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
    }

    if (window.themeVariables.settings.pageType !== "cart") { return }
    window.location.reload()
  }
};
if (!window.customElements.get('line-item-variant-selector')) {
  window.customElements.define('line-item-variant-selector', LineItemVariantSelector);
}


var LineItemSellingPlanSelector = class extends HTMLElement {
  constructor() {
    super();
    this.selectWrapper = this.querySelector('.line-item__select-wrapper');
    this.select = this.querySelector('select');
    this.checkbox = this.querySelector('.subscribe-toggle');
    this.key = this.dataset.key;
    this.cartItem = null;

    // Event bindings
    if (this.checkbox) {
      this.checkbox.addEventListener('change', this._debounce(this.onToggleSubscribe, 300));
    }
    if (this.select) {
      this.select.addEventListener('change', this.onChangePlan.bind(this));
    }

    // Load cart, then init with the actual item state
    this.getCart();
  }

  /**
   * Debounce helper function. Delays invoking a function until after `delay`
   * milliseconds have passed since the last time it was invoked.
   * @param {Function} func The function to debounce.
   * @param {number} delay The number of milliseconds to delay.
   * @returns {Function} The new debounced function.
   */
  _debounce(func, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  async getCart() {
    const res = await fetch(`${Shopify.routes.root}cart.js`);
    const cartJson = await res.json();
    this.cartItems = cartJson.items || [];
    this.cartItem = this.cartItems.find(item => item.key === this.key);

    // Sync UI to current state (subscribed vs one-time)
    const isSubscribed = !!(this.cartItem && this.cartItem.selling_plan_allocation);
    this.setSelectVisibility(isSubscribed);

    if (this.select && isSubscribed && this.cartItem.selling_plan_allocation) {
      const currentId = this.cartItem.selling_plan_allocation.selling_plan.id;
      if (String(this.select.value) !== String(currentId)) {
        this.select.value = String(currentId);
      }
    }
  }

  setSelectVisibility(show) {
    if (!this.selectWrapper) {
      return;
    }

    if (show) {
      this.selectWrapper.removeAttribute('hidden');
      if (this.checkbox) {
        this.checkbox.setAttribute('aria-expanded', 'true');
      }
    } else {
      this.selectWrapper.setAttribute('hidden', '');
      if (this.checkbox) {
        this.checkbox.setAttribute('aria-expanded', 'false');
      }
    }
  }

  async onToggleSubscribe() {
    const wantsSubscribe = this.checkbox.checked;

    // Toggle the select UI
    this.setSelectVisibility(wantsSubscribe);

    // Build change payload: subscribe => set plan id; one-time => null
    const sellingPlanValue = wantsSubscribe
      ? Number(this.select.value)
      : null;

    await this.pushChange({
      id: this.key,
      selling_plan: sellingPlanValue,
      quantity: this.cartItem ? this.cartItem.quantity : undefined
    });

    document.dispatchEvent(new CustomEvent('gtmEvent:subscription_toggle', {
      detail: {
        is_selected: wantsSubscribe,
        selected_index: this.select ? this.select.selectedIndex : 0,
        variant_id: this.cartItem.variant_id, // The item key is now different, so look up by variant ID.
      },
    }));
  }

  async onChangePlan() {
    // Only meaningful if subscribed (checkbox on)
    if (!this.checkbox || !this.checkbox.checked) {
      return;
    }

    await this.pushChange({
      id: this.key,
      selling_plan: Number(this.select.value),
      quantity: this.cartItem ? this.cartItem.quantity : undefined
    });
  }

  async pushChange(changeData) {
    await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(changeData)
    });

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }))

    if (window.themeVariables.settings.pageType !== "cart") { return }
    window.location.reload()
  }
};
if (!window.customElements.get('line-item-selling-plan-selector')) {
  window.customElements.define('line-item-selling-plan-selector', LineItemSellingPlanSelector);
}

export {
  GiftWithPurchase
}

if (!window.customElements.get('custom-quick-buy')) {
  class CustomQuickBuy extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.innerHTML = `
        <slot name="trigger"></slot>
        <slot name="sold-out"></slot>
        <slot name="trigger-mask"></slot>
        <slot name="variant-list"></slot>
      `;
      this.hasVariants = false;
      this.cartDrawer = null;
    }

    /**
     * Getter for the displayVariants property.
     * @returns {boolean} - True if the data attribute is true, false otherwise.
     */
    get displayVariants() {
      return this.dataset.displayVariants == 'true';
    }

    /**
     * Setter for the displayVariants property.
     * When set, it updates `data-display-variants` attribute on the host element.
     * @param {boolean} value - The new state for displaying variants.
     */
    set displayVariants(value) {
      if (!this.hasVariants) {
        return;
      }
      this.setAttribute('data-display-variants', value);
    }

    /**
     * Gets the selected variant property from the host's dataset.
     * @returns {string | boolean} The ID of the selected variant, or `false` if no variant is selected.
     */
    get selectedVariant() {
      return this.dataset.selectedVariant || false;
    }

    /**
     * Sets or clears the selected variant, but only updates if multiple variants are available.
     *
     * If a truthy `value` is provided, this sets the `data-selected-variant` attribute.
     * If a falsy value is passed, this finds and unchecks the currently selected
     * radio input and removes the data attribute.
     *
     * @param {string | null | undefined} value - The ID of the variant to set, or a falsy value to clear it.
     */
    set selectedVariant(value) {
      if (!this.hasVariants) {
        // Any default variant attribute value must persist.
        return;
      }

      if (value) {
        this.setAttribute('data-selected-variant', value);
        return;
      }

      if (this.selectedVariant) {
        const selectedInput = this.getSelectedInput();
        selectedInput.checked = false;
        this.removeAttribute('data-selected-variant');
      }
    }

    /**
     * Finds and returns the currently selected variant input element.
     * @returns {HTMLInputElement | null} The selected input element, or `null` if not found.
     */
    getSelectedInput = () => {
      return this.querySelector(`input[name="id"][value="${this.selectedVariant}"]`);
    }

    /**
     * Closes the variant list if a click occurs outside of this component.
     * @param {MouseEvent} event
     */
    handleOutslideClick = (event) => {
      if (!this.displayVariants) {
        return;
      }
      if (!event.composedPath().includes(this)) {
        this.displayVariants = false;
        this.selectedVariant = null;
      }
    }

    /**
     * Handles keydown events for the component.
     * @param {KeyboardEvent} event
     */
    handleKeyDown = (event) => {
      if (event.key !== 'Enter' || !this.displayVariants) {
        return;
      }
      // Check if the focused element is a label within this component.
      const focusedElement = document.activeElement;
      if (focusedElement && focusedElement.tagName === 'LABEL' && this.contains(focusedElement)) {
        event.preventDefault();

        // Find the radio input associated with the focused label.
        const inputId = focusedElement.getAttribute('for');
        if (!inputId) {
          return;
        }

        const input = this.querySelector(`#${inputId}`);
        if (!input) {
          return;
        }

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }
    }

    /**
     * Adds one unit of the selected variant to the cart.
     */
    addToCart = () => {
      if (!this.selectedVariant) {
        console.error('Custom quick buy failed: No variant is selected.');
        return;
      }

      const { sellingPlanId } = this.getSelectedInput().dataset;

      const items = [
        {
          id: this.selectedVariant,
          quantity: 1,
          selling_plan: sellingPlanId || null,
        },
      ];

      this.setAttribute('aria-busy', true);

      fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items }),
      })
      .then((response) => response.json())
      .then((response) => {
        // Trigger the cart to refresh itself. If the cart is openedf while this happens then
        // scroll to the variant when it's been added.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: {
            lineItem: response?.items[0],
            scrollToItem: true,
          },
          bubbles: true
        }));
      })
      .catch(error => {
        this.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            error: error.message,
          }
        }));
      })
      .finally(() => {
        this.setAttribute('aria-busy', false);

        if (this.cartDrawer) {
          this.cartDrawer.show();
        } else {
          window.location.href = Shopify.routes.root + 'cart';
        }
      });
    }

    connectedCallback() {
      // The theme's cart drawer.
      this.cartDrawer = document.getElementById('cart-drawer');

      // Dismiss on any outside click.
      document.addEventListener('click', this.handleOutslideClick);

      // Handle keyboard events.
      this.addEventListener('keydown', this.handleKeyDown);

      // Connect variant list.
      const variantListSlot = this.shadowRoot.querySelector('slot[name="variant-list"]');
      variantListSlot.addEventListener('slotchange', () => {
        this.hasVariants = true;
      });

      // Connect quick add button.
      const triggerSlot = this.shadowRoot.querySelector('slot[name="trigger"]');
      triggerSlot.addEventListener('slotchange', () => {
        const element = triggerSlot.assignedElements()[0];

        element.addEventListener('click', (e) => {
          e.preventDefault();

          if (this.selectedVariant) {
            this.addToCart();
            return;
          }

          this.displayVariants = !this.displayVariants;
        });
      });

      this.addEventListener('change', (e) => {
        if (e.target.name === 'id') {
          this.selectedVariant = e.target.value;
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleOutslideClick);
      this.removeEventListener('keydown', this.handleKeyDown);
    }
  }

  customElements.define('custom-quick-buy', CustomQuickBuy);
}

/**
 * NC-539 - Impact Stat Carousel
 *
 * Announces slide changes in a polite live region. The Figma Interaction brief
 * asks for an announcement on change ("slide 2 of 3") and for focus to stay put,
 * which is what the theme carousel already does - it only lacks the
 * announcement. Kept generic so any theme carousel can be given one: point
 * `observes` at the carousel id and pass a translated `announcement-template`
 * carrying the [index] and [total] placeholders.
 *
 * The element is inert without JS. It renders empty, so a failed script costs
 * the announcement and nothing else.
 */
var CarouselAnnouncer = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();

    const carousel = this.observedCarousel;

    if (!carousel) {
      return;
    }

    carousel.addEventListener('carousel:change', this._onChange.bind(this), {
      signal: this._abortController.signal,
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  get observedCarousel() {
    return this.hasAttribute('observes')
      ? document.getElementById(this.getAttribute('observes'))
      : null;
  }

  get template() {
    return this.getAttribute('announcement-template') || '[index] / [total]';
  }

  _onChange(event) {
    const total = event.target.items?.length;

    if (!total) {
      return;
    }

    this.textContent = this.template
      .replace('[index]', event.detail.index + 1)
      .replace('[total]', total);
  }
};

if (!window.customElements.get('carousel-announcer')) {
  window.customElements.define('carousel-announcer', CarouselAnnouncer);
}

/**
 * NC-540 - Steps Timeline reveal behavior.
 *
 * Fades each stage row in as it comes into view: opacity 0 to 1 with an 8px
 * lift, 400ms ease-out, triggered once 25% of the row is visible. Rows that
 * enter together are staggered 80ms apart in source order, which reads top to
 * bottom on desktop and left to right on the mobile scroller. A row animates
 * once and does not replay on scroll back.
 *
 * The transition itself lives in assets/custom-theme.css. This element only
 * opts into it (the `data-reveal` attribute) and toggles `is-revealed`, so
 * when JS never runs the rows render in their finished state. Under
 * prefers-reduced-motion nothing is observed and no attribute is set, so
 * every row is visible immediately.
 *
 * It also measures the stage list and toggles `is-scrollable` on it, which is
 * how the scrollbar snippet's own `peer-not-scrollable:hidden` decides whether
 * to show the slider control. Measuring rather than counting blocks in Liquid
 * means the control is shown only when there is somewhere to scroll, and it
 * hides itself at desktop widths where the list is in static flow.
 *
 * Finally it publishes each stage's own and next marker widths as custom
 * properties. The connector between two stages holds a fixed clearance at each
 * end, so its length depends on both markers' widths - and a pill marker's
 * width is merchant copy, not a token. CSS cannot read a sibling's width, so
 * this is the one piece of the layout it cannot work out for itself. Both
 * properties fall back to the circle width in the stylesheet, so a render
 * without JS still draws every connector.
 */
var StepsTimeline = class extends HTMLElement {
  static STAGGER_MS = 80;
  static THRESHOLD = 0.25;

  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('.steps-timeline__step'));
    this.list = this.querySelector('.steps-timeline__list');

    if (!this.steps.length) {
      return;
    }

    this.markers = this.steps.map((step) => step.querySelector('.steps-timeline__marker'));
    this.publishedWidths = [];

    this.observeSizes();

    const canAnimate = window.matchMedia('(prefers-reduced-motion: no-preference)').matches
      && 'IntersectionObserver' in window;

    if (!canAnimate) {
      return;
    }

    // Only now do the rows start hidden, so a failed script cannot hide them.
    this.setAttribute('data-reveal', '');

    this.observer = new IntersectionObserver(
      this.handleIntersect,
      { threshold: StepsTimeline.THRESHOLD }
    );

    this.steps.forEach((step) => this.observer.observe(step));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  /**
   * Watches the list and every marker, so both the slider control's gating and
   * the connector widths keep up with a resize, a breakpoint change or a font
   * loading in and changing a pill's width.
   */
  observeSizes() {
    this.measure();

    if (!('ResizeObserver' in window)) {
      return;
    }

    this.resizeObserver = new ResizeObserver(this.measure);

    if (this.list) {
      this.resizeObserver.observe(this.list);
    }

    this.markers.forEach((marker) => marker && this.resizeObserver.observe(marker));
  }

  measure = () => {
    this.updateScrollable();
    this.publishMarkerWidths();
  }

  /**
   * Keeps `is-scrollable` on the stage list in step with whether the list
   * actually overflows, so the slider control appears only when it is useful.
   */
  updateScrollable() {
    if (!this.list) {
      return;
    }

    this.list.classList.toggle('is-scrollable', this.list.scrollWidth > this.list.clientWidth);
  }

  /**
   * Gives each stage its own and its next neighbour's marker width, which is
   * what the connector needs to hold an equal clearance at both ends. Only
   * writes when a width has actually changed, so this cannot feed itself
   * through the ResizeObserver.
   */
  publishMarkerWidths() {
    const widths = this.markers.map((marker) => (marker ? marker.getBoundingClientRect().width : 0));

    if (widths.every((width, index) => width === this.publishedWidths[index])) {
      return;
    }

    this.publishedWidths = widths;

    this.steps.forEach((step, index) => {
      step.style.setProperty('--steps-timeline-marker-width', `${widths[index]}px`);

      if (index + 1 < widths.length) {
        step.style.setProperty('--steps-timeline-next-marker-width', `${widths[index + 1]}px`);
      }
    });
  }

  /**
   * Reveals every row that has come into view, staggered in source order.
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersect = (entries) => {
    const entering = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => this.steps.indexOf(a.target) - this.steps.indexOf(b.target));

    entering.forEach((entry, index) => {
      const step = entry.target;
      step.style.setProperty('--steps-timeline-reveal-delay', `${index * StepsTimeline.STAGGER_MS}ms`);
      step.classList.add('is-revealed');
      // Each row animates once.
      this.observer.unobserve(step);
    });
  }
};
if (!window.customElements.get('steps-timeline')) {
  window.customElements.define('steps-timeline', StepsTimeline);
}

/**
 * NC-551 - Scrolling banner
 *
 * Marquee of short claims. See docs/specs/NC-551.md.
 *
 * The theme's built-in <marquee-text> cannot be reused: its duration factors in a
 * viewport-derived slowFactor, which makes the pixels-per-second rate change with
 * viewport width. This design calls for a constant rate tied to row width, plus a
 * pause control, so the behaviour lives here instead of in theme-owned theme.js.
 */
if (!customElements.get('scrolling-banner')) {
  const GAP = 16; // Matches the track gap in custom-theme.css.

  class ScrollingBanner extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.list = this.querySelector('[data-list]');
      this.toggle = this.querySelector('[data-toggle]');
      this.toggleLabel = this.querySelector('[data-toggle-label]');

      if (!this.track || !this.list) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this._onToggleClick = this._onToggleClick.bind(this);
      this._measure = this._measure.bind(this);

      if (this.toggle) {
        this.toggle.addEventListener('click', this._onToggleClick);
      }

      // Duration is a function of measured width, so anything that changes width has to
      // re-measure: block edits in the editor, and the Typekit webfont swap, which lands
      // after first paint and widens the row.
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this._measure);
        this.resizeObserver.observe(this.list);
      }

      window.addEventListener('resize', this._measure);
      this.reducedMotion.addEventListener('change', this._measure);

      this._measure();
    }

    disconnectedCallback() {
      if (this.toggle) {
        this.toggle.removeEventListener('click', this._onToggleClick);
      }

      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this._measure);
      this.reducedMotion?.removeEventListener('change', this._measure);
    }

    get speed() {
      // Pixels per second. Guarded so a zero or missing value cannot divide to Infinity.
      return Math.max(1, parseInt(this.getAttribute('speed'), 10) || 40);
    }

    _measure() {
      // Under reduced motion the CSS shows a static, hand-scrollable row. Cloning and
      // duration serve no purpose, and the clones would add horizontal scroll distance.
      if (this.reducedMotion.matches) {
        this._setCloneCount(0);
        this.style.removeProperty('--scrolling-banner-distance');
        this.style.removeProperty('--scrolling-banner-duration');
        return;
      }

      // offsetWidth, not getBoundingClientRect(): the latter reports the visual rect,
      // which includes any transform: scale() on an ancestor. The theme editor scales
      // the whole preview down to fit its panel, so a scaled measurement paired with a
      // keyframe that translates in unscaled layout pixels runs the row 1/scale too
      // fast. offsetWidth is in layout pixels and ignores ancestor transforms.
      const listWidth = this.list.offsetWidth;

      // A section rendered inside a hidden parent measures 0. Leave the properties
      // untouched rather than committing a 0s duration; the observer fires again once
      // the section becomes visible.
      if (listWidth === 0) return;

      // One repeat unit is a list plus the gap that follows it, so translating by exactly
      // this distance lands the next copy where the last one started.
      const distance = listWidth + GAP;

      // Fill the viewport twice over, so the loop never shows a gap at either edge.
      const needed = Math.ceil((distance + window.innerWidth) / distance);
      this._setCloneCount(Math.max(2, needed) - 2);

      this.style.setProperty('--scrolling-banner-distance', `${distance.toFixed(2)}px`);
      this.style.setProperty('--scrolling-banner-duration', `${(distance / this.speed).toFixed(3)}s`);
    }

    /**
     * Liquid renders two lists. This adds or removes further copies beyond those two.
     * Every clone is aria-hidden so the claims are announced once.
     */
    _setCloneCount(count) {
      const existing = this.track.querySelectorAll('[data-list-extra]');

      for (let i = count; i < existing.length; i++) {
        existing[i].remove();
      }

      for (let i = existing.length; i < count; i++) {
        const clone = this.list.cloneNode(true);
        clone.removeAttribute('data-list');
        clone.setAttribute('data-list-extra', '');
        clone.setAttribute('aria-hidden', 'true');

        // Clones come from the real list, so they carry its ids and editor block
        // attributes. Both have to go or the theme editor sees several elements
        // claiming the same block.
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone
          .querySelectorAll('[data-shopify-editor-block]')
          .forEach((node) => node.removeAttribute('data-shopify-editor-block'));

        this.track.appendChild(clone);
      }
    }

    _onToggleClick() {
      const paused = this.toggle.getAttribute('aria-pressed') === 'true';

      this.toggle.setAttribute('aria-pressed', paused ? 'false' : 'true');

      if (paused) {
        this.removeAttribute('data-paused');
      } else {
        this.setAttribute('data-paused', '');
      }

      if (this.toggleLabel) {
        const next = paused ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
        if (next) this.toggleLabel.textContent = next;
      }
    }
  }

  customElements.define('scrolling-banner', ScrollingBanner);
}

/*
 * Places the PDP rank badge under the Front Row sticker when a product has both.
 *
 * The two are one vertical stack in the design, but the sticker is a third-party
 * iframe: its size is fixed pixels rather than a share of the media box, and it
 * changes at runtime when the widget collapses itself. CSS percentages cannot
 * track that, so the badge is measured into place instead.
 *
 * Two things the rect does not tell us. The iframe is larger than the card it
 * paints, by 8px of transparent shadow padding on each side and along the bottom
 * in its own coordinates, so the bottom edge has to be pulled back by that much.
 * And the vendor scales the iframe above 769px, so the padding is read at the
 * scale actually applied - recovered from the rect rather than assumed, so a
 * change to their transform does not silently break the alignment.
 *
 * Does nothing when there is no sticker, which is most products. The percentages
 * in custom-theme.css are the standalone position and the no-JS fallback.
 */
const FRONTROW_SHADOW_PAD = 8;

// Gap between the card's bottom edge and the badge, as a share of the media box.
// Measured off the mockups: 20px on a 486px box, 8px on a 245px one.
const RANK_BADGE_STACK_GAP = { base: 0.033, desktop: 0.041 };

var ProductRankBadge = class extends HTMLElement {
  connectedCallback() {
    this.slide = this.closest('.product-gallery__media');
    this.sticker = this.slide && this.slide.querySelector('#frontrow-sticker');

    if (!this.slide || !this.sticker) return;

    this._align = this._align.bind(this);
    this._observer = new ResizeObserver(this._align);
    this._observer.observe(this.slide);
    this._observer.observe(this.sticker);
    window.addEventListener('resize', this._align);

    // The stylesheet holds the badge at opacity 0 until this attribute lands, so
    // it is never seen at the standalone position and then jumping. The timer is
    // the backstop: if the iframe is blocked or never loads, the badge still
    // appears, at the position custom-theme.css gives it.
    this._reveal = setTimeout(() => this.setAttribute('data-aligned', ''), 1200);

    this._align();
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    window.removeEventListener('resize', this._align);
    clearTimeout(this._reveal);
  }

  _align() {
    const sticker = this.sticker.getBoundingClientRect();
    const slide = this.slide.getBoundingClientRect();

    // The iframe has no size before it loads, and the widget can hide itself.
    // Hand the badge back to the stylesheet rather than pinning it to nothing.
    if (!sticker.width || !sticker.height || !slide.width) {
      this.style.removeProperty('inset-block-start');
      this.style.removeProperty('inset-inline-end');
      return;
    }

    // offsetWidth is the untransformed box, so this recovers the applied scale.
    const padding = FRONTROW_SHADOW_PAD * (sticker.width / this.sticker.offsetWidth);
    const gap = window.matchMedia('(min-width: 1000px)').matches
      ? RANK_BADGE_STACK_GAP.desktop
      : RANK_BADGE_STACK_GAP.base;

    // Horizontally the padding cancels out - it is symmetric, so the iframe's
    // centre is the card's centre.
    const cardCenter = (sticker.left + sticker.right) / 2;

    this.style.insetBlockStart = `${sticker.bottom - padding - slide.top + slide.width * gap}px`;
    this.style.insetInlineEnd = `${slide.right - cardCenter - this.offsetWidth / 2}px`;

    clearTimeout(this._reveal);
    this.setAttribute('data-aligned', '');
  }
};

if (!window.customElements.get('product-rank-badge')) {
  window.customElements.define('product-rank-badge', ProductRankBadge);
}

/*
 * Keeps `is-selected` on the BSTC gallery slide the carousel reports as current.
 *
 * The theme moves the class only when a slide is chosen through `select()`. A
 * slide reached by scrolling instead - tabbing onto it, or a trackpad swipe -
 * updates the carousel's index but leaves the class on the previous slide. The
 * BSTC zoom overlay is hidden off that class while a video is showing, so it
 * stayed over the video and a click opened the zoom on the first image.
 */
document.addEventListener('carousel:change', (event) => {
  const carousel = event.target;

  if (!carousel.matches('.product--bstc media-carousel')) return;

  carousel.items.forEach((item) => {
    item.classList.toggle('is-selected', item === event.detail.slide);
  });
});
